<div class="chat-shell">

  <!-- Turn list -->
  <div class="turn-list" #turnList>
    @for (turn of turns; track $index) {
      <div class="turn" [class.turn-user]="turn.role === 'user'" [class.turn-agent]="turn.role === 'agent'">
        <div class="bubble">
          @if (turn.edited) {
            <span class="bubble-text muted edit-marker">
              @if (turn.editedLetter) {
                {{ 'cv.tailor.chat.editedLetter' | translate }}
              } @else {
                {{ 'cv.tailor.chat.edited' | translate: { count: turn.editChangeCount ?? 0 } }}
              }
            </span>
          } @else if (turn.text) {
            <span class="bubble-text">{{ turn.text }}</span>
          } @else if (turn.role === 'agent') {
            @if (turn.proposal && isOpen(turn)) {
              <span class="bubble-text proposal-headline">
                @if (turn.proposal.summary?.length) {
                  {{ 'cv.tailor.chat.proposalChanges' | translate: { count: turn.proposal.summary.length } }}
                } @else {
                  {{ 'cv.tailor.chat.proposalRewrite' | translate }}
                }
              </span>
              @if (kind === 'cv') {
                <!-- The letter proposal renders in full in the left pane, so it
                     needs no jump; a CV proposal has its own review view.
                     Deliberately NOT the same target as "N changes applied"
                     below, which names the saved document's own changes. -->
                <button type="button" class="changes-link" (click)="viewProposal.emit()">
                  {{ 'cv.tailor.chat.reviewChanges' | translate }}
                  <nb-icon icon="diagonal-arrow-right-up-outline"></nb-icon>
                </button>
              }
              <div class="proposal-actions">
                <button nbButton status="success" size="tiny" type="button" [disabled]="turn.resolving" (click)="acceptProposal(turn)">
                  {{ 'cv.tailor.chat.accept' | translate }}
                </button>
                <button nbButton ghost status="basic" size="tiny" type="button" [disabled]="turn.resolving" (click)="rejectProposal(turn)">
                  {{ 'cv.tailor.chat.reject' | translate }}
                </button>
              </div>
            } @else if (turn.proposal) {
              <!-- Superseded: a later refine, or a direct edit in the workspace,
                   replaced this proposal server-side. Its Accept would answer
                   409 and its review link would open a DIFFERENT proposal's
                   diff, so it renders as what it has become. -->
              <span class="bubble-text muted">{{ 'cv.tailor.chat.rejected' | translate }}</span>
            } @else if (turn.proposalResolved) {
              <span class="bubble-text muted">
                {{ (turn.proposalResolved === 'accepted' ? 'cv.tailor.chat.accepted' : 'cv.tailor.chat.rejected') | translate }}
              </span>
            } @else if (turn.letterUpdated) {
              <span class="bubble-text">{{ 'cv.tailor.chat.letterUpdated' | translate }}</span>
            } @else if (turn.changeCount && turn.changeCount > 0) {
              <button type="button" class="changes-link" (click)="viewChanges.emit()">
                {{ 'cv.tailor.chat.changesApplied' | translate: { count: turn.changeCount } }}
                <nb-icon icon="diagonal-arrow-right-up-outline"></nb-icon>
              </button>
            } @else {
              <span class="bubble-text muted">{{ 'cv.tailor.chat.noChanges' | translate }}</span>
              @if (!turn.rejected && !turn.note) {
                <span class="bubble-text muted no-change-hint">{{ 'cv.tailor.chat.noChangesHint' | translate }}</span>
              }
            }
          }
          @if (turn.note) {
            <span class="bubble-text note-line">{{ turn.note }}</span>
          }
          @if (turn.rejected && turn.rejected.length > 0) {
            <ul class="rejected-list">
              @for (r of turn.rejected; track $index) {
                <li class="rejected-item">
                  @if (r.reason === 'dropped_quantified_fact') {
                    {{ 'cv.tailor.chat.keptFigures' | translate }}
                  } @else if (r.reason === 'ungrounded_causal_claim') {
                    {{ 'cv.tailor.chat.keptHonesty' | translate }}
                  } @else if (r.reason === 'collapsed_role_content') {
                    {{ 'cv.tailor.chat.keptContent' | translate }}
                  } @else {
                    {{ ('cv.tailor.chat.reject.' + (r.reason === 'locked op not unlocked' ? 'locked'
                        : r.reason === 'pinned role cannot be dropped' ? 'pinned'
                        : 'generic')) | translate }}
                  }
                </li>
              }
            </ul>
          }
        </div>
      </div>
    }
    @if (sending) {
      <div class="turn turn-agent">
        <div class="bubble bubble-thinking">
          <span class="typing-dots" aria-label="{{ 'cv.tailor.chat.thinking' | translate }}">
            <span></span><span></span><span></span>
          </span>
        </div>
      </div>
    }
  </div>

  <!-- Turn counter -->
  <div class="turn-counter">
    {{ 'cv.tailor.chat.turnCounter' | translate: { turnCount: turnCount, maxTurns: maxTurns } }}
  </div>

  <!-- Max reached hint -->
  @if (atLimit) {
    <p class="max-reached-hint">{{ 'cv.tailor.chat.maxReached' | translate }}</p>
  }

  <!-- Input area -->
  <div class="input-area">
    <textarea
      nbInput
      fullWidth
      rows="3"
      [(ngModel)]="instruction"
      [disabled]="atLimit || sending || disabled"
      [placeholder]="'cv.tailor.chat.placeholder' | translate"
      (keydown)="onKeydown($event)">
    </textarea>

    <div class="input-actions">
      <!-- Advanced toggles -->
      <div class="advanced-section">
        <button
          nbButton
          ghost
          size="tiny"
          status="basic"
          type="button"
          [nbTooltip]="'cv.tailor.chat.unlockHint' | translate"
          (click)="showAdvanced = !showAdvanced">
          {{ 'cv.tailor.chat.advanced' | translate }}
          <nb-icon [icon]="showAdvanced ? 'chevron-up-outline' : 'chevron-down-outline'"></nb-icon>
        </button>

        @if (showAdvanced) {
          <div class="advanced-panel">
            <nb-toggle [(ngModel)]="unlockRoleTitle">
              {{ 'cv.tailor.chat.unlockRoleTitle' | translate }}
            </nb-toggle>
            <nb-toggle [(ngModel)]="unlockSkillLabel">
              {{ 'cv.tailor.chat.unlockSkillLabel' | translate }}
            </nb-toggle>
          </div>
        }
      </div>

      <button
        nbButton
        status="primary"
        type="button"
        [disabled]="atLimit || sending || !instruction.trim() || disabled"
        [nbSpinner]="sending"
        nbSpinnerStatus="basic"
        (click)="send()">
        {{ 'cv.tailor.chat.send' | translate }}
      </button>
    </div>
  </div>

</div>
